<template>
  <div ref="buttonListRef" class="relative inline-flex">
    <ButtonBase
      :id="id"
      :type-style="typeStyle"
      :color="color"
      :size="buttonSize"
      :class-add="'gap-2 ' + buttonClassAdd"
      v-bind="attrs"
      @click="toggleMenu"
    >
      <component v-if="buttonIcon" :is="iconos[buttonIcon]" class="h-4 w-4" />
      {{ label }}
      <ChevronDownIcon class="h-4 w-4 transition-transform" :class="{ 'rotate-180': isOpen }" />
    </ButtonBase>

    <Transition
      enter-active-class="transition-all duration-150 ease-out"
      enter-from-class="opacity-0 translate-y-1 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-100 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-1 scale-95"
    >
      <div
        v-if="isOpen"
        role="menu"
        :class="['absolute top-full right-0 z-40 mt-2 origin-top-right overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800', dropdownWidth]"
      >
        <div v-if="title" class="py-3 px-5 bg-gray-100 rounded-t-xl dark:bg-gray-700">
          <p class="text-sm text-gray-500 dark:text-gray-400">{{ title }}</p>
          <p v-if="subTitle" class="text-sm font-medium text-gray-800 dark:text-gray-300">{{ subTitle }}</p>
        </div>
        <template v-if="optionsComponents">
          <div
            v-for="(item, index) in items"
            :key="index"
            class="w-full transition hover:bg-gray-100 dark:hover:bg-gray-700/40"
          >
            <component :is="(item as any)" />
          </div>
        </template>
        <template v-else>
          <div
            v-if="withFilter"
            class="sticky top-0 z-10 bg-white dark:bg-gray-800 p-2 border-b border-gray-100 dark:border-gray-700"
            @click.stop
          >
            <InputDefault
              v-model="stringSearch"
              placeholder="Buscar..."
              type="text"
              class="w-full"
              @click.stop
              @keydown.stop
              @input.stop
            />
          </div>
          <div :class="withFilter ? 'max-h-60 overflow-y-auto' : ''">
            <div
              v-if="withFilter && filteredItems.length === 0 && stringSearch.trim()"
              class="py-2 px-3 text-sm text-gray-500 dark:text-gray-400 text-center"
            >
              No se encontraron resultados
            </div>
            <button
              v-for="item in filteredItems"
              :key="(item as IButtonListItem).label"
              type="button"
              class="flex w-full cursor-pointer items-center gap-2 px-4 py-3 text-left text-sm text-gray-700 transition hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700/40"
              @click="handleItemClick((item as IButtonListItem).clickEvent)"
            >
              <component
                v-if="(item as IButtonListItem).icon"
                :is="iconos[(item as IButtonListItem).icon!]"
                class="h-4 w-4 text-gray-500 dark:text-gray-300"
              />
              <span>{{ (item as IButtonListItem).label }}</span>
            </button>
          </div>
        </template>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, useAttrs } from 'vue'

defineOptions({ inheritAttrs: false })
const attrs = useAttrs()
import type { IconName } from '@/modules/Core/interfaces'
import * as iconos from '@heroicons/vue/24/outline'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'
import ButtonBase from '@/modules/Core/components/general/ButtonBase.vue'
import InputDefault from '@/modules/Core/components/Form/InputDefault.vue'

export interface IButtonListItem {
  label: string
  icon?: IconName
  clickEvent: () => void
  show?: boolean
}

const props = withDefaults(defineProps<{
  id: string
  label?: string
  buttonIcon?: IconName
  items: unknown[]
  color?: string
  typeStyle?: string
  buttonClassAdd?: string
  optionsComponents?: boolean
  withFilter?: boolean
  title?: string
  subTitle?: string
  dropdownWidth?: string
  buttonSize?: string
}>(), {
  label: '',
  color: 'blue-list',
  typeStyle: 'white-variant',
  buttonClassAdd: '',
  optionsComponents: false,
  withFilter: false,
  dropdownWidth: 'w-64',
  buttonSize: 'sm'
})

const isOpen = ref(false)
const buttonListRef = ref<HTMLElement | null>(null)
const stringSearch = ref('')

const filteredItems = computed(() => {
  const visible = props.items.filter(item => (item as IButtonListItem).show !== false)
  if (!props.withFilter || !stringSearch.value.trim()) return visible
  return visible.filter(item => {
    const label = (item as IButtonListItem).label
    return label?.toLowerCase().includes(stringSearch.value.toLowerCase())
  })
})

const closeMenu = () => {
  isOpen.value = false
  stringSearch.value = ''
}

const toggleMenu = () => {
  isOpen.value = !isOpen.value
}

const handleItemClick = (clickEvent: () => void) => {
  closeMenu()
  clickEvent()
}

const handleClickOutside = (event: Event) => {
  if (!isOpen.value || !buttonListRef.value) return
  const target = event.target as Node | null
  if (target && !buttonListRef.value.contains(target)) {
    closeMenu()
  }
}

const handleEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleEscape)
})
</script>
